import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationStart, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TooltipDirective } from '@catch/directives';
import { ScreenService, ThemingService } from '@catch/services';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faArrowRightArrowLeft,
  faBriefcase,
  faEllipsis,
  faFileLines,
  faGear,
  faHome,
  faSearch,
  faUser,
} from '@fortawesome/pro-light-svg-icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PopoverModule } from 'primeng/popover';
import { filter } from 'rxjs/operators';
import { PLATFORM_EMPTY_USER } from '../../models/platform.models';
import { PlatformCmdkService } from '../../services/platform-cmdk.service';
import { PlatformAuthService } from '../../services/platform-auth.service';
import { PlatformSidenavService } from '../../services/platform-sidenav.service';
import { PlatformAvatarPlaceholderComponent } from '../avatar-placeholder/platform-avatar-placeholder.component';
import { PlatformCmdkComponent } from '../cmdk/platform-cmdk.component';
import { PlatformKbdComponent } from '../kbd/platform-kbd.component';
import { PlatformSidenavGroupComponent } from '../sidenav-group/platform-sidenav-group.component';
import { PlatformSidenavSubItem } from '../sidenav-group/platform-sidenav-group.models';
import { PlatformSidenavUserComponent } from '../sidenav-user/platform-sidenav-user.component';

@UntilDestroy()
@Component({
  selector: 'app-platform-sidenav',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    RouterLinkActive,
    FontAwesomeModule,
    TranslocoPipe,
    TooltipDirective,
    PopoverModule,
    PlatformKbdComponent,
    PlatformCmdkComponent,
    PlatformAvatarPlaceholderComponent,
    PlatformSidenavGroupComponent,
    PlatformSidenavUserComponent,
  ],
  templateUrl: './platform-sidenav.component.html',
  styleUrl: './platform-sidenav.component.scss',
})
export class PlatformSidenavComponent {
  private readonly router = inject(Router);
  private readonly screenService = inject(ScreenService);
  private readonly document = inject(DOCUMENT);
  private readonly cmdkService = inject(PlatformCmdkService);

  readonly sidenavService = inject(PlatformSidenavService);
  readonly themingService = inject(ThemingService);
  private readonly auth = inject(PlatformAuthService);

  readonly icons = {
    search: faSearch,
    home: faHome,
    applications: faFileLines,
    briefcase: faBriefcase,
    user: faUser,
    settings: faGear,
    switch: faArrowRightArrowLeft,
    ellipsis: faEllipsis,
  };

  readonly profileChildren: readonly PlatformSidenavSubItem[] = [
    {
      labelKey: 'jobboard.platform.profile.section-nav.contact',
      fragment: 'contact',
    },
    {
      labelKey: 'jobboard.platform.profile.section-nav.preferences',
      fragment: 'preferences',
    },
    {
      labelKey: 'jobboard.platform.profile.section-nav.social',
      fragment: 'social',
    },
    {
      labelKey: 'jobboard.platform.profile.section-nav.documents',
      fragment: 'documents',
    },
    {
      labelKey: 'jobboard.platform.profile.section-nav.skills',
      fragment: 'skills',
    },
    {
      labelKey: 'jobboard.platform.profile.section-nav.languages',
      fragment: 'languages',
    },
  ];

  readonly settingsChildren: readonly PlatformSidenavSubItem[] = [
    {
      labelKey: 'jobboard.platform.settings.section-nav.visibility',
      fragment: 'visibility',
    },
    {
      labelKey: 'jobboard.platform.settings.section-nav.notifications',
      fragment: 'notifications',
    },
    {
      labelKey: 'jobboard.platform.settings.section-nav.password',
      fragment: 'password',
    },
    {
      labelKey: 'jobboard.platform.settings.section-nav.tfa',
      fragment: 'tfa',
    },
    {
      labelKey: 'jobboard.platform.settings.section-nav.delete',
      fragment: 'delete',
    },
  ];

  readonly user = computed(
    () => this.auth.user() ?? PLATFORM_EMPTY_USER
  );

  readonly isSmallScreen = toSignal(this.screenService.isSmallScreen$, {
    initialValue: this.screenService.isSmallScreen,
  });

  readonly isSidenavCollapsed = computed(
    () => !this.sidenavService.sidenavOpen() && !this.isSmallScreen()
  );

  private readonly mobileShell = viewChild<ElementRef<HTMLElement>>('mobileShell');

  readonly popoverStyleClass = computed(() => {
    const classes = ['sidenav-popover'];
    if (!this.isSmallScreen() && !this.sidenavService.sidenavOpen()) {
      classes.push('collapsed');
    }
    return classes.join(' ');
  });

  readonly popoverAppendTo = computed(() => {
    if (!this.isSmallScreen()) {
      return 'body';
    }
    return this.mobileShell()?.nativeElement ?? 'body';
  });

  readonly isMac = computed(() =>
    (this.document.defaultView?.navigator.platform ?? '')
      .toLowerCase()
      .includes('mac')
  );

  readonly cmdkKeys = computed(() =>
    this.isMac() ? (['⌘', 'K'] as const) : (['Strg', 'K'] as const)
  );

  readonly cmdkAriaLabel = computed(() =>
    this.isMac() ? 'Command + K' : 'Control + K'
  );

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        untilDestroyed(this)
      )
      .subscribe(() => {
        if (this.isSmallScreen()) {
          this.sidenavService.closeSidenav();
        }
      });
  }

  /**
   * Opens the command palette from the sidenav search field.
   * @param event - Optional mouse or keyboard event
   */
  openCmdkFromSidenav(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.cmdkService.openCmdk();
  }

  /**
   * Returns initials for the user avatar.
   * @returns Uppercase initials
   */
  userInitials(): string {
    return `${this.user().firstName?.[0] ?? ''}${this.user().lastName?.[0] ?? ''}`.toUpperCase();
  }
}
